import React, { useEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import PatientPickerSection, { usePatientPicker } from './PatientPickerSection';
import OtRoomSection from './OtRoomSection';
import DepartmentDoctorSection, { newDoctorRow } from './DepartmentDoctorSection';
import ServiceSelectionSection from './ServiceSelectionSection';
import PatientConsumptionSection from './PatientConsumptionSection';
import LabRadChargesSection from './LabRadChargesSection';
import InvButton from '../inventory/components/InvButton';
import { INV_SPACING } from '../inventory/theme';
import { OT_PAGE_SX, OT_SUBMIT_BAR_SX, OT_SUBMIT_BTN_SX } from './otTheme';
import { createConsumption, confirmConsumption } from '../inventory/api/dispensing';
import { captureChargesAfterPatientConfirm } from '../inventory/utils/consumptionChargeCapture';
import { signAndSendInvestigations } from './labRadOrderApi';
import { captureOtCharges } from '../ipd-billing/billing-api';
import { buildOtChargePayload } from './otChargeApi';
import { fetchCasesForAdmission } from '../ot/api/otApi';

async function submitConsumptionBranch(body, lines) {
  const created = await createConsumption(body);
  const id = created?.transaction_id || created?.id;
  if (!id) throw new Error('Consumption was created but no id was returned');
  await confirmConsumption(id, { remarks: body.remarks || '' });
  await captureChargesAfterPatientConfirm({
    ...created,
    id,
    transaction_id: id,
    consumption_type: 'PATIENT',
    admission_id: body.admission_id,
    items: lines || body.items,
  });
}

function settledMessage(name, result) {
  if (result.status === 'fulfilled') return `${name} saved`;
  return `${name}: ${result.reason?.message || result.reason || 'Failed'}`;
}

export default function OtChargeCapture() {
  const patientPicker = usePatientPicker();
  const { patient } = patientPicker;
  const admissionId = patient?.admission_id;
  const consumptionApiRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [duration, setDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('minute');
  const [service, setService] = useState(null);
  const [doctors, setDoctors] = useState([newDoctorRow()]);
  const [labItems, setLabItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [activeOtCase, setActiveOtCase] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!admissionId) {
      setActiveOtCase(null);
      return;
    }
    fetchCasesForAdmission(admissionId)
      .then((res) => {
        if (cancelled) return;
        const cases = res?.data || [];
        const matched = cases.find((c) => c.admission_id === admissionId) || cases[0] || null;
        setActiveOtCase(matched);
      })
      .catch(() => {
        if (!cancelled) setActiveOtCase(null);
      });
    return () => {
      cancelled = true;
    };
  }, [admissionId]);

  async function handleSubmit() {
    if (!patient || !admissionId) {
      toast.error('Select a patient to continue');
      return;
    }

    const durationNum = Number(duration);
    if (room && !(durationNum > 0)) {
      toast.error('Enter OT room duration');
      return;
    }
    if (!room && duration !== '' && durationNum > 0) {
      toast.error('Select an OT room for the duration entered');
      return;
    }

    const billedDoctors = doctors.filter(d => d.doctorId && Number(d.amount) > 0);
    if (service && billedDoctors.length === 0) {
      toast.error('Enter a billing amount for at least one doctor');
      return;
    }
    if (!service && billedDoctors.length > 0) {
      toast.error('Select a surgical procedure for the doctor charges');
      return;
    }

    const consumption = consumptionApiRef.current?.collect?.() || { skipped: true };
    if (consumption.error) {
      toast.error(consumption.error);
      return;
    }

    const baseOtPayload = buildOtChargePayload({ room, duration, durationUnit, service, doctors });
    const otPayload =
      baseOtPayload && activeOtCase
        ? { ...baseOtPayload, ot_case_id: activeOtCase.id }
        : baseOtPayload;
    const hasLab = labItems.length > 0;
    const hasConsumption = Boolean(consumption.body);
    const hasOt = Boolean(otPayload);

    if (!hasOt && !hasLab && !hasConsumption) {
      toast.error('Add an OT room, surgery, consumption, or lab/rad item before submitting');
      return;
    }

    if (hasLab && !patient.care_context_ref) {
      toast.error('Cannot send lab/rad orders — missing care context on this admission');
      return;
    }

    const tasks = [];
    const names = [];

    if (hasConsumption) {
      names.push('Consumption');
      tasks.push(submitConsumptionBranch(consumption.body, consumption.lines));
    }
    if (hasLab) {
      names.push('Lab & Rad');
      tasks.push(signAndSendInvestigations({
        pendingItems: labItems,
        admissionId,
        careContextId: patient.care_context_ref,
        doctorName: patient.doctor_name,
      }));
    }
    if (hasOt) {
      names.push('OT charges');
      tasks.push(captureOtCharges(admissionId, otPayload));
    }

    setSubmitting(true);
    try {
      const results = await Promise.allSettled(tasks);
      const failed = results.filter(r => r.status === 'rejected');
      const succeeded = results.filter(r => r.status === 'fulfilled');

      results.forEach((result, i) => {
        if (result.status === 'rejected') {
          toast.error(settledMessage(names[i], result));
          return;
        }
        if (names[i] === 'OT charges') {
          setRoom(null);
          setDuration('');
          setDurationUnit('minute');
          setService(null);
          setDoctors([newDoctorRow()]);
        } else if (names[i] === 'Lab & Rad') {
          setLabItems([]);
        } else if (names[i] === 'Consumption') {
          consumptionApiRef.current?.reset?.();
        }
      });

      if (failed.length === 0) {
        toast.success('OT charge capture submitted');
      } else if (succeeded.length > 0) {
        toast.warning(`${succeeded.length} of ${results.length} sections saved. Failed sections were left on the form.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={OT_PAGE_SX}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: INV_SPACING.section }}>
        <PatientPickerSection picker={patientPicker} />

        {activeOtCase && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: '#eff6ff',
              borderRadius: 1.5,
              border: '1px solid #bfdbfe',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>
              Linked OT Case: {activeOtCase.case_number || `#${activeOtCase.id}`} ({activeOtCase.procedure_name || activeOtCase.procedures?.[0]?.procedure_name || 'Scheduled Surgery'})
            </Typography>
          </Box>
        )}

        <OtRoomSection
          room={room}
          onRoomChange={setRoom}
          duration={duration}
          onDurationChange={setDuration}
          durationUnit={durationUnit}
          onDurationUnitChange={setDurationUnit}
        />

        <ServiceSelectionSection
          service={service}
          onServiceChange={setService}
        />

        <DepartmentDoctorSection
          doctors={doctors}
          onDoctorsChange={setDoctors}
        />

        <PatientConsumptionSection patient={patient} formApiRef={consumptionApiRef} />

        <LabRadChargesSection
          patient={patient}
          pendingItems={labItems}
          onPendingItemsChange={setLabItems}
        />

        <Box sx={OT_SUBMIT_BAR_SX}>
          {/* The span is required: a disabled button fires no pointer events,
              so the tooltip needs a wrapper that does. */}
          <Tooltip title={!patient && !submitting ? 'Select a patient before submitting charges.' : ''}>
            <span style={{ display: 'inline-flex' }}>
              <InvButton onClick={handleSubmit} disabled={!patient || submitting} loading={submitting} sx={OT_SUBMIT_BTN_SX}>
                {submitting ? 'Submitting…' : 'Submit'}
              </InvButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
